import vuetify from "@/plugins/vuetify";
import * as dfd from 'danfojs';
import * as XLSX from 'xlsx';
import parser from 'any-date-parser';
import { parquetReadObjects } from 'hyparquet';
import { compressors } from 'hyparquet-compressors';
import { asyncBufferFrom } from 'hyperparam';

async function handleJsonFile(file) {
    try {
        const text = await file.text();
        const parsed = JSON.parse(text);

        if (!Array.isArray(parsed)) {
            throw new Error(vuetify.framework.lang.t('$vuetify.gpt_dataset.invalid_json_error'));
        }

        if (parsed.length === 0) {
            return new dfd.DataFrame([]);
        }

        if (typeof parsed[0] !== 'object' || parsed[0] === null) {
            throw new Error(vuetify.framework.lang.t('$vuetify.gpt_dataset.invalid_json_item_error'));
        }

        return new dfd.DataFrame(parsed);
    } catch (e) {
        throw new Error(vuetify.framework.lang.t('$vuetify.gpt_dataset.invalid_json_error_prefix') + e.message);
    }
}

function getDuplicateHeaders(headers) {
    const seenItems = new Set();
    const duplicatesSet = new Set();

    for (const item of headers) {
        if (seenItems.has(item)) {
            duplicatesSet.add(item);
        } else {
            seenItems.add(item);
        }
    }

    const duplicates = Array.from(duplicatesSet);
    return duplicates;
}

async function handleExcelFile(file) {
    const data = await file.arrayBuffer();

    const workbook = XLSX.read(data, {
        type: 'array',
        cellNF: true
    });

    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];

    // --- STEP 1: Get the complete list of headers ---
    const rowsAsArrays = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    if (rowsAsArrays.length < 1) {
        return new dfd.DataFrame([]);
    }
    const allHeaders = rowsAsArrays[0];

    const duplicates = getDuplicateHeaders(allHeaders);
    if (duplicates.length > 0) {
        throw new Error(`${vuetify.framework.lang.t('$vuetify.gpt_dataset.duplicate_column_error')} ${duplicates.join(', ')}`);
    }

    // --- STEP 2: Get the data as an array of potentially incomplete objects ---
    const dataAsObjects = XLSX.utils.sheet_to_json(worksheet, { raw: false });

    // --- STEP 3: Normalize the data ---
    // For each row object, ensuring value for all headers are present, inserting null if not.
    const uniformData = dataAsObjects.map(incompleteRow => {
        const completeRow = {}; // Start with a fresh, empty object for this row.

        // Iterate through list of all headers.
        allHeaders.forEach(header => {
            // Check if the header exists as a key in the original row object.
            if (Object.prototype.hasOwnProperty.call(incompleteRow, header)) {
                // If exists, copy its value.
                completeRow[header] = incompleteRow[header];
            } else {
                // If it does NOT exist, this is a missing value for that column. Inserting null.
                completeRow[header] = null;
            }
        });
        return completeRow;
    });

    // --- STEP 4: Create the DataFrame ---
    // Danfo.js now can infer the columns from the keys of new, *uniform* objects.
    return new dfd.DataFrame(uniformData);
}

function checkDuplicateColumnsParquet(columnNames){
    const pattern = /_duplicated_\d+$/;

    const columnsWithPattern = columnNames
        .filter(key => pattern.test(key))
        .map(key => key.replace(pattern, ''));

    if (columnsWithPattern.length > 0) {
        throw new Error(`${vuetify.framework.lang.t('$vuetify.gpt_dataset.duplicate_column_error')} ${columnsWithPattern.join(', ')}`);
    }
}

function normalizeParquetData(parquetData) {
    return parquetData.map((row, index) => {
        const normalizedRow = {};
        for (const [key, value] of Object.entries(row)) {
            if (typeof value === 'bigint') {
                if (value > Number.MAX_SAFE_INTEGER || value < Number.MIN_SAFE_INTEGER) {
                    normalizedRow[key] = value.toString();
                } else {
                    normalizedRow[key] = Number(value);
                }
            } else {
                normalizedRow[key] = value ?? null;
            }
        }
        return normalizedRow;
    });
}

async function handleParquetFile(file) {
    try {
        const asyncBuffer = await asyncBufferFrom({ file, byteLength: file.size });
        const parquetData = await parquetReadObjects({ file: asyncBuffer, compressors });

        if (!Array.isArray(parquetData) || parquetData.length === 0) {
            throw new Error(vuetify.framework.lang.t('$vuetify.gpt_dataset.parquet_file_corrupted_error'));
        }

        checkDuplicateColumnsParquet(Object.keys(parquetData[0]));

        const normalizedData = normalizeParquetData(parquetData);
        return new dfd.DataFrame(normalizedData);
    } catch (err) {
        if (err?.message) {
            throw err;
        }
        throw new Error(vuetify.framework.lang.t('$vuetify.gpt_dataset.file_corrupted_error'));
    }
}

function checkDuplicateHeadersCSV(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (event) => {
            try {
                const firstLine = event.target.result.split(/\r\n|\n/)[0];
                const headers = firstLine.split(/,|\t/);

                const duplicates = getDuplicateHeaders(headers);
                if (duplicates.length > 0) {
                    return reject(
                        new Error(
                            `${vuetify.framework.lang.t('$vuetify.gpt_dataset.duplicate_column_error')} ${duplicates.join(', ')}`
                        )
                    );
                }
                resolve();
            } catch (err) {
                reject(err);
            }
        };

        reader.onerror = () => {
            reject(new Error(vuetify.framework.lang.t('$vuetify.gpt_dataset.csv_parsing_error')));
        };

        reader.readAsText(file.slice(0, 4096));
    });
}

export async function processFile(file) {
    const fileExtension = file.name.split('.').pop().toLowerCase();
    try {
        if (fileExtension === 'csv') {
            try {
                await checkDuplicateHeadersCSV(file);

                const df = await dfd.readCSV(file);
                return df;
            } catch (err) {
                if (err?.message) {
                    throw err;
                }
                throw new Error(
                    vuetify.framework.lang.t('$vuetify.gpt_dataset.csv_parsing_error')
                );
            }
        }
        else if (fileExtension === 'json') {
            return await handleJsonFile(file);
        }
        else if (fileExtension === 'xlsx') {
            return await handleExcelFile(file);
        }
        else if (fileExtension === 'parquet') {
            return await handleParquetFile(file);
        } else {
            throw new Error(vuetify.framework.lang.t('$vuetify.gpt_dataset.unsupported_file_error') + ` ${fileExtension}`);
        }
    } catch (err) {
        if (err?.message) {
            throw err;
        }
        throw new Error(vuetify.framework.lang.t('$vuetify.gpt_dataset.file_process_error'));
    }
}

export function generateFilePreview(df, previewRows = 5) {
    previewRows = Math.min(df.shape[0], previewRows);
    const headers = df.columns;
    const rows = [];

    for (let i = 0; i < previewRows; i++) {
        const row = headers.map(col => df.at(i, col));
        rows.push(row);
    }

    return { headers, rows };
}

export function formatPreviewValue(value) {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string' && value.trim() === '') return '';
    if (typeof value === 'string' && value.length > 50) return value.substring(0, 47) + '...';
    return String(value);
}

export function isValidDateTime(value) {
    if (!value) return false;

    if (value instanceof Date && !isNaN(value.getTime())) {
        return true;
    }

    if (typeof value === 'string' && isNaN(Number(value.trim()))) {
        let potentialDate = parser.fromString(value.trim());
        if (potentialDate.isValid()) {
            return true;
        }
    }

    return false;
}
export function isValidDateTimeStrict(value) {
    if (!value) return false;

    if (value instanceof Date && !isNaN(value.getTime())) {
        return true;
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();

        const isoDateOrDateTimeRegex = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{1,2}:\d{1,2}(?::\d{1,2}(?:\.\d{1,6})?)?(?:Z|[+-]\d{2}:\d{2})?)?$/;

        if (!isoDateOrDateTimeRegex.test(trimmed)) {
            return false;
        }

        const potentialDate = parser.fromString(trimmed);
        return potentialDate.isValid();
    }

    return false;
}


function isDatetimeColumn(values) {
    const nonNullValues = values.filter(val =>
        val !== null &&
        val !== undefined &&
        val !== '' &&
        !(typeof val === 'string' && val.trim() === '')
    );

    if (nonNullValues.length === 0) return false;

    const validDateCount = nonNullValues.reduce((count, val) => {
        if (isValidDateTime(val)) {
            return count + 1;
        }
        return count;
    }, 0);

    return (validDateCount / nonNullValues.length) >= 1.0;
}

function isBooleanColumn(values) {
    if (!values || values.length === 0) {
        return false;
    }

    const validBooleanStrings = new Set(['true', 'false']);
    return values.every(val => {
        const normalizedVal = String(val).toLowerCase().trim();
        // Check if the normalized string is either 'true' or 'false'.
        return validBooleanStrings.has(normalizedVal);
    });
}

function isTextColumn(values) {
    return values.some(val => isNaN(Number(val)));
}

function isDecimalColumn(values) {
    return values.some(val => !Number.isInteger(Number(val)));
}

function isIntegerColumn(values) {
    return values.every(val => Number.isInteger(Number(val)));
}

export function generateColumnStats(df, sampleSize = 100) {
    const columnNames = df.columns;
    const totalRows = df.shape[0];
    sampleSize = Math.min(totalRows, sampleSize);
    const datetimeColumns = [];

    let columnStats = columnNames.map((col) => {
        const originalSeries = df[col];
        const nullCount = originalSeries.isNa().sum();
        const nullPercentage = Math.round((nullCount / totalRows) * 100);

        let uniqueCount;
        try {
            uniqueCount = originalSeries.unique().shape[0];
        } catch (e) {
            try {
                let stringSeries = originalSeries.asType('string');
                uniqueCount = stringSeries.unique().shape[0];
            } catch (error) {
                console.warn(`Could not compute unique count for column "${col}":`, error);
                uniqueCount = 0;
            }
        }

        let sampleValues = [];
        try {
            let naDroppedSeries = originalSeries.dropNa();

            // filtering empty or whitespace-only strings
            const filteredArr = naDroppedSeries.values.filter(val => {
                if (typeof val === "string") {
                    return val.trim() !== "";
                }
                return true;
            });

            sampleValues = filteredArr.slice(0, sampleSize);
        } catch (e) {
            try {
                let stringSeries = originalSeries.asType('string');
                let naDroppedSeries = stringSeries.dropNa();

                // filtering empty or whitespace-only strings
                const filteredArr = naDroppedSeries.values.filter(val => {
                    if (typeof val === "string") {
                        return val.trim() !== "";
                    }
                    return true;
                });

                sampleValues = filteredArr.slice(0, sampleSize);
            } catch (error) {
                console.warn(`Could not get sample values for column "${col}":`, error);
            }
        }

        let inferredType;
        if (sampleValues.length === 0) {
            inferredType = 'null';
        }
        else if (isDatetimeColumn(sampleValues)) {
            inferredType = 'datetime';
            datetimeColumns.push(col);
        }
        else if (isBooleanColumn(sampleValues)) {
            inferredType = 'boolean';
        }
        else if (isTextColumn(sampleValues)) {
            inferredType = 'text';
        }
        else if (isDecimalColumn(sampleValues)) {
            inferredType = 'decimal';
        }
        else {
            inferredType = 'integer';
        }

        const uniqueValuesFromSample = [...new Set(sampleValues)];
        const previewValues = uniqueValuesFromSample
            .slice(0, 3)
            .map(val => formatPreviewValue(val));

        return {
            column: col,
            inferredType,   // Based on *sample* (deliberately), to *display* backend behavior closely
            nullCount,      // Based on original
            nullPercentage, // Based on original
            uniqueCount,    // Based on original
            sampleValues: previewValues.length > 0 ? previewValues : [''] // Based on sample
        };
    });
    return { columnStats, datetimeColumns };
}

export function getTypeColor(type) {
    const colorMap = {
        'integer': 'blue',
        'decimal': 'teal',
        'datetime': 'green',
        'text': 'orange',
        'boolean': 'purple',
        'object': 'red',
        'null': 'grey'
    };
    return colorMap[type] || 'grey';
}

export function castValue(value) {
    if (typeof value !== "string") {
        return value;
    }

    const trimmed = value.trim();

    if (trimmed.toLowerCase() === "null" || trimmed === "") return null;
    if (trimmed.toLowerCase() === "true") return true;
    if (trimmed.toLowerCase() === "false") return false;

    if (!isNaN(trimmed) && trimmed !== "") {
        return Number(trimmed);
    }
    if ((trimmed.startsWith("{") && trimmed.endsWith("}")) ||
        (trimmed.startsWith("[") && trimmed.endsWith("]"))) {
        try {
            return JSON.parse(trimmed);
        } catch (e) {
            return value;
        }
    }
    return value;
}
